import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { http, fmtDate } from "@/lib/api";
import { ClockClockwise, FunnelSimple, X, DownloadSimple } from "@phosphor-icons/react";

const ACTION_LABELS = {
  created: "Created", approved: "Approved", rejected: "Rejected",
  assigned: "Assigned", multi_assigned: "Multi-assigned", accepted: "Accepted",
  started: "Started", paused: "Paused", resumed: "Resumed", returned: "Returned",
  completed: "Completed", closed: "Closed", photos_added: "Photos added",
  sla_breached: "SLA breached", feedback: "Feedback",
  stage_added: "Stage added", stage_updated: "Stage updated", stage_deleted: "Stage deleted",
};

export default function AuditLog() {
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [jobId, setJobId] = useState("");

  const params = {};
  if (userId) params.user_id = userId;
  if (action) params.action = action;
  if (jobId) params.job_id = jobId;

  const { data: entries = [], isLoading, error } = useQuery({
    queryKey: ["audit-log", userId, action, jobId],
    queryFn: () => http.get("/audit-log", { params }).then((r) => r.data),
    retry: false,
  });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });
  const { data: jobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: () => http.get("/jobs").then(r => r.data) });

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);
  const jobMap = useMemo(() => Object.fromEntries(jobs.map((j) => [j.id, j])), [jobs]);

  const distinctActions = useMemo(() => Array.from(new Set(entries.map(e => e.action))).sort(), [entries]);
  const hasActiveFilter = !!(userId || action || jobId);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await http.get("/audit-log/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (error) return <div className="p-8 text-sm text-rose-600" data-testid="audit-forbidden">You don't have permission to view the audit log.</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="overline">Compliance & Audit</div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1 mb-1">Audit log</h1>
      <p className="text-sm text-slate-500 mb-5">Cross-job event history. Use the filters to investigate who did what and when.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-slate-200">
        <FunnelSimple size={14} className="text-slate-500" />
        <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="audit-user-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          <option value="">All users</option>
          <option value="system">System</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          <option value="">All actions</option>
          {distinctActions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="audit-job-filter"
          className="h-9 px-3 text-sm border border-slate-200 rounded-md outline-none focus:border-[#002FA7] bg-white">
          <option value="">All jobs</option>
          {jobs.slice(0, 200).map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.title}</option>)}
        </select>
        {hasActiveFilter && (
          <button onClick={() => { setUserId(""); setAction(""); setJobId(""); }} data-testid="audit-filters-clear"
            className="inline-flex items-center gap-1 h-9 px-3 text-[11px] text-slate-500 hover:text-rose-600">
            <X size={11} /> Clear filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <div className="text-[11px] text-slate-500 font-mono-data">{entries.length} entries</div>
          <button onClick={exportCsv} disabled={exporting || entries.length === 0} data-testid="audit-export-csv-btn"
            className="inline-flex items-center gap-1.5 h-9 px-3 text-[12px] font-medium border border-slate-200 rounded-md bg-white text-slate-700 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            <DownloadSimple size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-2.5 w-40">When</th>
              <th className="px-4 py-2.5">User</th>
              <th className="px-4 py-2.5">Action</th>
              <th className="px-4 py-2.5">Job</th>
              <th className="px-4 py-2.5">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">Loading…</td></tr>}
            {!isLoading && entries.map((e, i) => {
              const job = jobMap[e.job_id];
              return (
                <tr key={`${e.at}-${i}`} data-testid={`audit-row-${i}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-[12px] font-mono-data text-slate-600 whitespace-nowrap">
                    <div className="flex items-center gap-1.5"><ClockClockwise size={11} />{fmtDate(e.at)}</div>
                  </td>
                  <td className="px-4 py-2.5 text-[12px]">{e.user_id === "system" ? <span className="text-slate-500 italic">System</span> : (userMap[e.user_id] || <span className="text-slate-400 font-mono-data text-[10px]">{e.user_id}</span>)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-heading uppercase tracking-wider font-semibold border rounded-sm bg-slate-50 text-slate-700 border-slate-200">
                      {ACTION_LABELS[e.action] || e.action.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[12px]">
                    {job ? (
                      <Link to={`/jobs/${e.job_id}`} className="font-mono-data hover:text-[#002FA7]">{job.job_number}</Link>
                    ) : <span className="text-slate-400 font-mono-data text-[10px]">{e.job_id?.slice(0, 8)}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-slate-600">
                    {e.details && Object.keys(e.details).length > 0
                      ? <span className="font-mono-data text-[11px]">{Object.entries(e.details).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              );
            })}
            {!isLoading && entries.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500" data-testid="audit-empty">No events match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
